import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Code} from 'src/design/atoms/Code';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import type {FC, ReactNode} from 'react';
import type {DeficiencyResponse} from 'src/api/ProactApiClient';
import {splitAttachmentsByImage} from 'src/app/inspections/attachmentHelpers';
import {Pill} from 'src/design/atoms/Pill';
import {ThumbnailStrip} from 'src/design/molecules/ThumbnailStrip';
import {useAppSelector} from 'src/redux/hooks';
import {DeficiencyStatePill} from 'src/shared/constants/PillConstants';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';

export interface DeficiencyCardProps {
  deficiency: DeficiencyResponse;
  /** Trailing action slot — admin's Resolve button. Omitted entirely (not passed, not
   *  disabled) for read-only consumers — there's no button-shaped hole left behind. */
  action?: ReactNode;
}

/**
 * Single deficiency card: device details, note, attachments. Shared identically between
 * admin and inspector views; only the trailing action slot differs.
 */
export const DeficiencyCard: FC<DeficiencyCardProps> = ({deficiency, action}) => {
  const {accessToken} = useAppSelector(state => state.auth);
  const {images: imageAttachments, files: fileAttachments} =
    splitAttachmentsByImage(deficiency.attachments);

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      p="sm"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderLeft: '4px solid var(--mantine-color-red-6)',
        borderRadius: 4,
      }}>
      <Box style={{minWidth: 0, flex: 1}}>
        <Group gap="xs" wrap="nowrap">
          <Text data-testid="deficiency-device-name" fw={500} size="sm" truncate>
            {deficiency.deviceName}
            {deficiency.deviceIsDeleted ? ' (deleted)' : ''}
          </Text>
          <Pill
            {...DeficiencyStatePill(deficiency.status, deficiency.resolutionSource)}
          />
        </Group>

        {deficiency.equipmentTypeName && (
          <Text size="xs" c="dimmed">
            {deficiency.equipmentTypeName}
          </Text>
        )}

        {deficiency.serialNumber && <Code>{deficiency.serialNumber}</Code>}

        <Text size="xs" c="dimmed" truncate>
          {[deficiency.building, deficiency.floor, deficiency.room]
            .filter(Boolean)
            .join(' · ') || 'Unspecified location'}
        </Text>

        {deficiency.comments && (
          <Box mt={4} pl="xs" style={{borderLeft: '2px solid var(--mantine-color-gray-4)'}}>
            <Text size="xs" c="dimmed" fs="italic">
              Inspector note — {deficiency.comments}
            </Text>
          </Box>
        )}

        {imageAttachments.length > 0 && (
          <Box mt={4}>
            <ThumbnailStrip
              items={imageAttachments.map(attachment => ({
                src: getUrlWithEncodingAndAuth(attachment.contentUrl, accessToken),
                alt: attachment.name,
              }))}
              countLabel={`${imageAttachments.length} photo${
                imageAttachments.length === 1 ? '' : 's'
              }`}
            />
          </Box>
        )}

        {fileAttachments.length > 0 && (
          <Group gap="xs" mt={4}>
            {fileAttachments.map(attachment => (
              <Anchor
                key={attachment.id}
                href={getUrlWithEncodingAndAuth(attachment.contentUrl, accessToken)}
                target="_blank"
                rel="noreferrer"
                size="xs">
                {attachment.name}
              </Anchor>
            ))}
          </Group>
        )}
      </Box>

      {action}
    </Group>
  );
};
